import axios from 'axios'
import { createAxiosInstance } from 'common/config/axios.config'
import { getMallConfig } from 'common/config/mall'
import { CommonCode } from 'domain/common/model'
import { CarouselModel, CategoryModel, ProductModel } from 'domain/goods/model'
import { ProductResponseDTO } from 'domain/goods/response.dto'
import { ErrorHandler } from 'common/utils/error-handler.util'
import { CacheUtil } from 'common/utils/cache.util'

const goodsAxiosInstance = createAxiosInstance(false)

export function fetchProduct(
  productId: number | string,
): Promise<ProductModel> {
  return goodsAxiosInstance
    .get<ProductResponseDTO>(`/goods/products/${productId}`)
    .then((response) => {
      return new ProductModel(response.data)
    })
    .catch((error) => {
      throw ErrorHandler.handleApiError(error);
    })
}

export function fetchCategories(): Promise<CategoryModel[]> {
  return goodsAxiosInstance
    .get('/goods/categories')
    .then((response) => response.data)
    .catch((error) => {
      throw ErrorHandler.handleApiError(error);
    })
}

export function fetchCategory(
  categoryId: number | string,
): Promise<CategoryModel> {
  return goodsAxiosInstance
    .get(`/goods/categories/${categoryId}`)
    .then((response) => response.data)
}

export function fetchAllProductList(): Promise<ProductModel[]> {
  return goodsAxiosInstance
    .get('/goods/products')
    .then((response) => response.data)
}

export function fetchProductList(
  categoryId: number | string,
): Promise<ProductModel[]> {
  return goodsAxiosInstance
    .get(`/goods/categories/${categoryId}/products`)
    .then((response) => response.data)
}

export function getCommonCodeFetch(): Promise<CommonCode[]> {
  return goodsAxiosInstance
    .get(`/common/product`)
    .then((response) => response.data)
}

export async function fetchCarousel(
  itemLength: number,
): Promise<CarouselModel[]> {
  try {
    const mallId = localStorage.getItem('mall-id') || String(getMallConfig().id);
    
    // API 호출
    const response = await goodsAxiosInstance.get(`/slides`, {
      params: { mall_id: mallId }
    });
    
    const data = response.data.map((item: any) => ({
      id: item.id.toString(),
      author: item.description || '',
      download_url: item.code_value || '', // PC 이미지는 code_value에 저장됨
      mobile_url: item.code_value2 || '', // 모바일 이미지는 code_value2에 저장됨
      url: item.code_value2 || '', // 모바일용 URL
      height: 0,
      width: 0
    }));
    
    // 결과 캐싱
    const cacheKey = `carousel_${mallId}`;
    CacheUtil.setItem(cacheKey, data, 10 * 60 * 1000); // 10분 캐시
    
    return data;
  } catch (error) {
    // 에러가 발생하면 캐시된 데이터 사용
    const mallId = localStorage.getItem('mall-id') || String(getMallConfig().id);
    const cacheKey = `carousel_${mallId}`;
    const cachedData = CacheUtil.getItem<CarouselModel[]>(cacheKey);
    
    if (cachedData) {
      return cachedData;
    }
    
    // 캐시도 없는 경우 더미 데이터 생성
    const dummyData = [
      {
        id: '1',
        author: 'Dummy Slide 1',
        download_url: 'https://halabet-s3.s3.ap-northeast-2.amazonaws.com/common/5c1ceadc-5bd1-4d21-8e33-d99d6a0e70d1.jpg',
        mobile_url: 'https://halabet-s3.s3.ap-northeast-2.amazonaws.com/common/5c1ceadc-5bd1-4d21-8e33-d99d6a0e70d1.jpg',
        url: 'https://halabet-s3.s3.ap-northeast-2.amazonaws.com/common/5c1ceadc-5bd1-4d21-8e33-d99d6a0e70d1.jpg',
        height: 500,
        width: 1200
      }
    ];
    
    // 더미 데이터 캐싱
    try {
      const mallId = localStorage.getItem('mall-id') || String(getMallConfig().id);
      const cacheKey = `carousel_${mallId}`;
      localStorage.setItem(cacheKey, JSON.stringify(dummyData));
    } catch (e) {
      console.warn('더미 데이터 캐싱 실패', e);
    }
    
    return dummyData;
  }
}

export function fetchRecommendProducts(): Promise<ProductModel[]> {
  const mallId = localStorage.getItem('mall-id') || String(getMallConfig().id);
  
  console.log('추천 상품 API 호출 시도...');
  return goodsAxiosInstance
    .get('/goods/products/recommend', {
      params: { mall_id: mallId }
    })
    .then((response) => {
      console.log('추천 상품 API 응답:', response.data);
      const data = response.data;
      
      // 캐싱
      try {
        localStorage.setItem('recommend_products', JSON.stringify(data));
      } catch (e) {
        console.warn('추천 상품 캐시 저장 실패', e);
      }
      
      return data;
    })
    .catch(error => {
      console.error('추천 상품 데이터를 가져오는데 실패했습니다', error);
      
      // 캐시 확인
      try {
        const cachedData = localStorage.getItem('recommend_products');
        if (cachedData) {
          console.log('추천 상품 캐시 데이터 사용');
          return JSON.parse(cachedData);
        }
      } catch (e) {
        console.warn('추천 상품 캐시 로드 실패', e);
      }
      
      // 더미 데이터
      console.log('더미 추천 상품 데이터 생성');
      return [];
    });
}
